import React, { useState } from "react";
import { Space, Form, Input, Button, Modal, Tooltip, Switch } from "antd";
import {
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  FileMarkdownOutlined
} from "@ant-design/icons";
import { formatCurrency } from "helper/util";
import { useDispatch, useSelector } from "react-redux";
import { updatePrice } from "../../store/RouteThunks";
import { allowPermission } from "helper/util";
import { canEditPermission } from "configs/constants";
import { selectRole } from "modules/auth/store/authSlice";
import RouteEstimateHistoryModal from "./RouteEstimateHistoryModal";

const EditPriceInput = ({
  value,
  record,
  priceType,
  typeName,
  manualUpdate,
  disabled = false,
  showIcon = true,
  iconOnly = false,
  onRefresh
}) => {
  const [editKey, setEditKey] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [pendingPriceUpdate, setPendingPriceUpdate] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const dispatch = useDispatch();
  const canEdit = allowPermission(useSelector(selectRole), canEditPermission);
  const [form] = Form.useForm();
  const [explanationForm] = Form.useForm();

  // Determine the estimate type based on priceType
  const getEstimateType = () => {
    if (priceType === "clientEstimatePrice" || priceType === "clientFinalPrice") {
      return "client_estimate";
    }
    if (priceType === "driverEstimatePrice" || priceType === "driverFinalPrice") {
      return "driver_estimate";
    }
    if (priceType === "priceHistory") {
      return "all"; // Special case for showing all price history
    }
    return null;
  };

  const handleHistoryIconClick = (e) => {
    e.stopPropagation();
    setShowHistoryModal(true);
  };

  const handleHistoryModalClose = () => {
    setShowHistoryModal(false);
  };

  const checkEditRow = (name, row) => {
    return editKey === name && row?.id === selectedRoute?.id;
  };

  const onFinish = (values) => {
    if (typeof values === "object") {
      const { typeClient, typeDriver, price } = values;
      // Show explanation modal for all price changes (estimates and finals)
      setPendingPriceUpdate({
        id: selectedRoute?.id,
        price,
        type: typeClient || typeDriver,
        user: record?.order?.user,
        clientName: record?.client?.clientName
      });
      setShowExplanationModal(true);
    }
  };

  const handleExplanationSubmit = async (explanationValues, e) => {
    e?.stopPropagation();
    if (pendingPriceUpdate && explanationValues.priceChangeExplanation) {
      try {
        await dispatch(
          updatePrice({
            ...pendingPriceUpdate,
            priceChangeExplanation: explanationValues.priceChangeExplanation,
            updateRelatedPrice: explanationValues.updateRelatedPrice !== false
          })
        ).unwrap();

        // Refresh the route list after successful update
        if (onRefresh) {
          onRefresh();
        }

        form?.resetFields();
        explanationForm?.resetFields();
        setSelectedRoute(null);
        setEditKey(null);
        setPendingPriceUpdate(null);
        setShowExplanationModal(false);
      } catch (error) {
        console.error("Error updating price:", error);
      }
    }
  };

  // Get the related price type label for the toggle
  const getRelatedPriceLabel = () => {
    if (!pendingPriceUpdate) return "";
    const type = pendingPriceUpdate.type;

    if (type === "clientEstimatePrice" || type === "driverEstimatePrice") {
      return "Final Price";
    } else if (type === "clientFinalPrice" || type === "driverFinalPrice") {
      return "Estimate Price";
    }
    return "";
  };

  const handleExplanationCancel = (e) => {
    e?.stopPropagation();
    explanationForm?.resetFields();
    setPendingPriceUpdate(null);
    setShowExplanationModal(false);
  };

  if (
    record.pickupTime === "null" &&
    record.jobs?.length === 1 &&
    record.jobs[0].pickupAddress === record.jobs[0].dropoffAddress
  ) {
    return <></>;
  }

  // If iconOnly mode, just show the icon
  if (iconOnly) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        {manualUpdate && showIcon && (
          <Tooltip title="Price Edit History">
            <FileMarkdownOutlined
              onClick={handleHistoryIconClick}
              style={{ cursor: "pointer", color: "#1890ff", fontSize: "24px" }}
            />
          </Tooltip>
        )}
        <RouteEstimateHistoryModal
          visible={showHistoryModal}
          onClose={handleHistoryModalClose}
          routeId={record?.id}
          estimateType={getEstimateType()}
        />
      </div>
    );
  }

  return (
    <Form
      onFinish={onFinish}
      form={form}
      initialValues={{
        remember: true
      }}
    >
      <Space>
        <div>
          {checkEditRow(priceType, record) ? (
            <Space onClick={(e) => e.stopPropagation()}>
              <Form.Item
                style={{ marginTop: "16px" }}
                span={4}
                name="price"
                rules={[
                  {
                    required: true,
                    message: "Please input your values!"
                  }
                ]}
              >
                <Input
                  defaultValue={value}
                  prefix={window.CURRENCY_SYMBOL}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: "90px"
                  }}
                />
              </Form.Item>
              <Form.Item
                name={typeName}
                style={{ display: "none" }}
                initialValue={priceType}
              ></Form.Item>

              <Button
                htmlType="submit"
                type="text"
                onClick={(e) => e.stopPropagation()}
                style={{
                  color: "green",
                  backgroundColor: "inherit",
                  marginLeft: "-16px"
                }}
              >
                <CheckOutlined />
              </Button>

              <CloseOutlined
                style={{ color: "#cecece", marginLeft: "-8px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRoute(null);
                  setEditKey(null);
                }}
              />
            </Space>
          ) : (
            <Space>
              <span>
                {window.CURRENCY_SYMBOL}
                {value || 0}
              </span>
              {canEdit && (
                <>
                  {!disabled && (
                    <EditOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRoute(record);
                        setEditKey(priceType);
                      }}
                    />
                  )}
                </>
              )}
            </Space>
          )}
        </div>
      </Space>

      {/* Price Change Explanation Modal */}
      <Modal
        title="Price Change Explanation"
        visible={showExplanationModal}
        onCancel={handleExplanationCancel}
        width={600}
        footer={null}
        onClick={(e) => e.stopPropagation()}
        getContainer={() => document.body}
      >
        <Form
          form={explanationForm}
          onFinish={handleExplanationSubmit}
          layout="vertical"
          onClick={(e) => e.stopPropagation()}
          initialValues={{
            updateRelatedPrice: false
          }}
        >
          <Form.Item
            name="priceChangeExplanation"
            label="Please explain why the price is being changed:"
            rules={[
              {
                required: true,
                message: "Price change explanation is required!"
              }
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter detailed explanation for the price change (e.g., additional waiting time, extra services, route modifications, etc.)"
              maxLength={500}
            />
          </Form.Item>
          <Form.Item
            name="updateRelatedPrice"
            label={`Also update ${getRelatedPriceLabel()}`}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                onClick={(e) => e.stopPropagation()}
              >
                Save Price Change
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleExplanationCancel(e);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Form>
  );
};

export default EditPriceInput;
